import type {
  AudienceBreakdownData,
  AudienceGroupCounts,
} from '../components/AudienceBreakdownSection'

/**
 * Which channels the composer currently has ticked.
 */
export type SelectedChannels = {
  postToFeed: boolean
  sendViaEmail: boolean
  sendViaPush: boolean
}

/**
 * What the currently-checked channels actually reach (SN-7901, decision 4).
 *
 * Derived here rather than in a component so the donut, its legend and the pre-send
 * confirmation are three reads of ONE calculation. That modal is the last screen before
 * an irreversible send; if it disagrees with the panel behind it, the feature has failed
 * its main requirement.
 *
 * The server sends both reaches in one payload, so toggling a channel never invalidates
 * the calculation and never needs a refetch.
 */
export type AudienceReach = {
  /** Centre of the donut: everyone the checked channels reach. */
  total: number
  /** How many of those hold an account. */
  joined: number
  /** How many are on a roster with no account — zero unless Email is on and the switch is on. */
  unjoined: number
  /** Reachable in-app / by push, for the footnote line. */
  inApp: number
  /** On a roster, no account, no address anywhere. Reported, never mailed. */
  noEmail: number
  /** Un-joined people the switch WOULD add, whether or not it is currently on. */
  unjoinedAvailable: number
  /**
   * Everyone this post targets, however they were found — accounts, roster people without
   * one, and roster people with no address at all.
   */
  inAudience: number
  /** Human list of the ticked channels, for the "Reach for: …" caption. */
  channelLabels: string[]
  /**
   * Per-group reach, on the same rule as `total`, so the donut's slices sum to its
   * centre. Keyed by the API's `*_users` names so the chart config needs no translation.
   */
  byGroup: Record<string, number>
  /**
   * Per-group MEMBERSHIP — how many people of each group are in the recipient list,
   * whether or not any channel reaches them. Keyed the same way.
   *
   * This is what decides which breakdown rows exist (see utils/audienceGroups); `byGroup`
   * decides what they say. It is deliberately not a deliverability number: it sums the
   * same three populations the centre's "of N in audience" counts, so the legend cannot
   * disagree with it, and it survives the non-production domain guard driving reach to 0.
   */
  inAudienceByGroup: Record<string, number>
}

/**
 * The audience payload names groups two ways: `reach.by_group` uses the resolver's names
 * (`admins`, `team_officials`) while the legacy per-group counts the chart is configured
 * against use `admin_users`, `team_official_users`. Neither is a pluralization of the
 * other, so the bridge is spelled out rather than derived.
 */
const GROUP_TO_CHART_KEY: Record<string, keyof AudienceGroupCounts> = {
  owners: 'owner_users',
  admins: 'admin_users',
  league_admins: 'league_admin_users',
  videographers: 'videographer_users',
  officials: 'official_users',
  team_officials: 'team_official_users',
  players: 'player_users',
  following: 'following_users',
}

const EMPTY: AudienceReach = {
  total: 0,
  joined: 0,
  unjoined: 0,
  inApp: 0,
  noEmail: 0,
  unjoinedAvailable: 0,
  inAudience: 0,
  channelLabels: [],
  byGroup: {},
  inAudienceByGroup: {},
}

/**
 * `reach` and `unjoined` are additive blocks; an older API build (or a cached response
 * from before this ticket) simply will not have them, and the panel must still render the
 * joined numbers rather than showing NaN.
 */
const reachOf = (data: AudienceBreakdownData) => ({
  inApp: data.reach?.in_app ?? data.unique_users ?? 0,
  emailJoined: data.reach?.email_joined ?? data.unique_users ?? 0,
  emailUnjoined: data.reach?.email_unjoined ?? 0,
  pushJoined: data.reach?.push_joined ?? data.unique_users ?? 0,
  noEmail: data.unjoined?.no_email ?? 0,
})

export const calculateAudienceReach = (
  data: AudienceBreakdownData | null | undefined,
  channels: SelectedChannels,
  includeUnjoined: boolean
): AudienceReach => {
  if (!data) {
    return EMPTY
  }

  const reach = reachOf(data)
  const { postToFeed, sendViaEmail, sendViaPush } = channels

  const channelLabels: string[] = []
  if (postToFeed) {
    channelLabels.push('Social Feed')
  }
  if (sendViaEmail) {
    channelLabels.push('Email')
  }
  if (sendViaPush) {
    channelLabels.push('Push Notification')
  }

  const unjoined = sendViaEmail && includeUnjoined ? reach.emailUnjoined : 0

  /*
   * A joined person is reached if ANY ticked channel reaches them:
   *
   *  - Push ticked  -> everyone in the audience gets at least the in-app notification.
   *    The `database` channel is allowed regardless of preference, so push_joined only
   *    describes the device alert, not the delivery.
   *  - Feed ticked  -> the post is published where the whole audience can read it.
   *  - Email only   -> down to the people who have not turned social email off.
   *
   * A union of the underlying sets is not derivable from three counts, but it is not
   * needed: whenever Feed or Push is on, the in-app reach already covers everyone.
   */
  const joined =
    postToFeed || sendViaPush
      ? reach.inApp
      : sendViaEmail
      ? reach.emailJoined
      : 0

  /*
   * Each slice gets the SAME treatment as the centre, so the legend can never sum to
   * something other than the number in the middle. Before this the slices read the raw
   * audience counts — neither domain-guarded nor preference-filtered — which is how a
   * legend totalling 130 could sit beside a centre reading 0.
   */
  const byGroup: Record<string, number> = {}
  const inAudienceByGroup: Record<string, number> = {}
  const groups = data.reach?.by_group

  /*
   * No fallback when `by_group` is missing, deliberately.
   *
   * `by_group` arrived with this ticket, so a response from an API build that predates it
   * has no per-group axis at all. `inAudienceByGroup` then stays empty, `audienceLegendRows`
   * selects no rows, and the panel degrades to the centre alone with no legend under it.
   *
   * The rows used to be seeded from the legacy `*_users` counts in that case — but only the
   * ROWS. Nothing can value a row without a per-group axis, so `byGroup` stayed empty and
   * every seeded row read 0 beside a centre reading the real total. A legend of zeroes next
   * to a non-zero centre is the precise disagreement this module exists to prevent, and it
   * is worse than no legend because it looks like an answer.
   *
   * If a fallback is ever reintroduced it must key on the field being ABSENT and never on
   * its being empty: the controller always sends the object, so `{}` is a real answer — an
   * audience nobody survives the domain guard — and reading that as "old build" would put
   * the legacy counts back on every guard-filtered org.
   */

  for (const [group, counts] of Object.entries(groups ?? {})) {
    const chartKey = GROUP_TO_CHART_KEY[group]
    if (!chartKey) {
      continue
    }

    const joinedPart =
      postToFeed || sendViaPush
        ? counts.in_app
        : sendViaEmail
        ? counts.email_joined
        : 0
    const unjoinedPart =
      sendViaEmail && includeUnjoined ? counts.email_unjoined : 0

    byGroup[chartKey] = joinedPart + unjoinedPart
    /*
     * Per-bucket fallback for the narrower case: a build that had `by_group` but not yet
     * the `in_audience` axis. The legacy count is the closest thing it can offer.
     */
    inAudienceByGroup[chartKey] = counts.in_audience ?? data[chartKey] ?? 0
  }

  return {
    total: joined + unjoined,
    joined,
    unjoined,
    inApp: reach.inApp,
    noEmail: reach.noEmail,
    unjoinedAvailable: reach.emailUnjoined,
    /*
     * Counted across all three populations, and deliberately NOT channel-scoped.
     *
     * The legacy `unique_users` counts people with accounts only, so once this ticket
     * started reaching roster people without one, the centre's "Recipients" could
     * exceed its "of N in audience" — a number larger than the group it is drawn
     * from, which reads as a bug however sound the arithmetic. Everyone targeted is now counted, so reach is
     * always the smaller number and the gap is exactly the people who cannot be reached.
     *
     * Held constant as channels and the switch move, so the denominator does not shift
     * under the reader while they are comparing options.
     *
     * Served by the API as the AUDIENCE — joined + un-joined + the people no channel can
     * reach — which is exactly what "of N in audience" wants. It is NOT the length of the
     * recipient list, as this comment used to say: since Sep 4 that list excludes the
     * unreachable, and the two differ by precisely `reach.noEmail`. The local fallback
     * below has always added that third term, which is the giveaway that the sentence was
     * describing the wrong quantity rather than the code doing the wrong thing.
     *
     * Deriving it locally counted audience user ids, some of which no longer resolve to a
     * person — so the panel claimed 1,276 beside a list of 1,274 people. The local sum is
     * only a fallback for a response from an API build that predates the field.
     */
    inAudience:
      data.reach?.in_audience ??
      (data.unique_users ?? 0) + reach.emailUnjoined + reach.noEmail,
    channelLabels,
    byGroup,
    inAudienceByGroup,
  }
}

/**
 * Sub-label under the "Include people who haven't joined" switch.
 *
 * Greyed at zero rather than hidden — a control whose explanation vanishes reads as a bug.
 */
export const unjoinedSwitchSubtitle = (count: number): string =>
  count > 0
    ? `Adds ${count.toLocaleString()} more ${
        count === 1 ? 'recipient' : 'recipients'
      }`
    : 'No additional recipients to add'

/**
 * The definition that sits under the switch.
 *
 * "Include people who haven't joined" prompted "haven't joined what?" in review — the
 * label named the outcome without naming who it meant. Spelling out the mechanism, in the
 * product's own words, is what makes it unambiguous.
 */
export const UNJOINED_SWITCH_TITLE = 'Include Non-SportNinja Users'

export const UNJOINED_SWITCH_DESCRIPTION =
  'Targets people who have an email address in SportNinja but have not accepted their invitation to the platform.'

/**
 * The footnote under the donut legend: what Feed and Push reach out of the total, shown
 * only when Email is not the sole channel (otherwise it is the same number twice).
 */
export const inAppFootnote = (
  reach: AudienceReach,
  channels: SelectedChannels
): string | null => {
  if (!channels.postToFeed && !channels.sendViaPush) {
    return null
  }

  return `Feed & Push reach ${reach.inApp.toLocaleString()} of these`
}
